import React from 'react'
import { CircularProgress, makeStyles, Collapse } from '@material-ui/core'
import moment from 'moment'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'

import Button from '../button/Button'
import StyledSVG from '../shared/StyledSVG'

const useStyles = makeStyles(theme => ({
  root: {
    paddingBottom: '20px'
  },
  first_row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 24
  },
  first_row_first_column: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '& h1': {
      fontWeight: 600,
      fontSize: 24
    }
  },
  first_row_second_column: {
    display: 'flex',
    alignItems: 'center',
    gap: 32
  },
  answers_total_wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '& p': {
      fontWeight: 500,
      fontSize: 16
    }
  },
  answers_total: {
    width: 56,
    height: 56,
    borderRadius: 56,
    backgroundColor: '#00ACDB',
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  go_back: {
    width: 180,
    border: '1px solid #386093',
    height: 44,
    color: '#386093',
    borderRadius: 8,
    '&:hover': {
      border: '1px solid #386093'
    },
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    lineHeight: '19.5px'
  },
  answers_wrapper: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 16
  },
  answer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  answer_wrapper: {
    padding: '18px 56px',
    backgroundColor: '#F5F6F7',
    borderRadius: 16
  },
  answer_first_column: {},
  answer_second_column: {
    display: 'flex',
    alignItems: 'center',
    columnGap: 56
  },
  answer_info: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& .title': {
      margin: 0,
      fontWeight: 500,
      fontSize: 14,
      color: '#868E96'
    },
    '& .value': {
      margin: 0,
      fontWeight: 600,
      fontSize: 20
    },
    '& .date': {
      width: 100,
      height: 35,
      borderRadius: 16,
      backgroundColor: '#ADB8CC',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 16
    }
  },
  expanded_icon: {
    cursor: 'pointer',
    transition: 'transform 0.3s ease-in-out'
  },
  rotate_arrow: {
    transform: 'rotate(180deg)'
  },
  answer_collapse_wrapper: {
    marginTop: 24,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 24
  },
  answer_collapse_root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 8,
    flex: '1 1 320px',
    maxWidth: 500,
    '& .topic': {
      fontWeight: 500,
      fontSize: 14,
      margin: 0
    }
  },
  answer_collapse: {
    display: 'flex',
    alignItems: 'center',
    columnGap: 12,
    '& p': {
      margin: 0,
      fontSize: 14
    },
    '& .answer': {
      fontWeight: 700,
      color: '#386093'
    },
    '& .author': {
      fontWeight: 400,
      color: '#868E96'
    }
  }
}))

const SpecificTagView = ({
  childId,
  selectedTag,
  setSelectedTag,
  getReportStudentsAnswers,
  cleanReportStudentAnswers,
  reportAnswers,
  tagIconSelect
}) => {
  const classes = useStyles()
  const tagIcon = tagIconSelect(selectedTag)
  const [expandedRows, setExpandedRows] = React.useState([])

  React.useEffect(() => {
    getReportStudentsAnswers({
      getAll: true,
      params: {
        'q[tag_name_eq]': selectedTag,
        'q[report_student_child_id_eq]': childId
      }
    })
    return () => {
      cleanReportStudentAnswers()
    }
  }, [])

  const answersByDate = React.useMemo(
    () => {
      if (reportAnswers?.items?.length > 0) {
        const groups = {}
        reportAnswers.items.forEach(answer => {
          const dateKey = moment(answer.created_at).format('DD/MM/YYYY')
          if (!groups[dateKey]) {
            groups[dateKey] = []
          }
          groups[dateKey].push(answer)
        })
        return Object.entries(groups)
      }
    },
    [reportAnswers.items]
  )

  const handleGoBack = () => {
    setSelectedTag(null)
  }

  const handleExpandRow = (target, date) => {
    setExpandedRows(prev => {
      const alreadyFilledRow = prev.find(item => item.date === date)
      if (alreadyFilledRow) {
        return prev.filter(item => {
          return item.date !== date
        })
      }
      return [...prev, { date, target }]
    })
  }

  if (reportAnswers.isFetching) return <CircularProgress />
  return (
    <div className={classes.root}>
      <div className={classes.first_row}>
        <div className={classes.first_row_first_column}>
          <StyledSVG src={tagIcon} width={40} height={40} />
          <h1>{selectedTag}</h1>
        </div>
        <div className={classes.first_row_second_column}>
          <div className={classes.answers_total_wrapper}>
            <p>Total de registros do(a) {String(selectedTag).toLowerCase()}:</p>
            <div className={classes.answers_total}>
              {reportAnswers.items.length}
            </div>
          </div>
          <Button
            variant='outlined'
            className={classes.go_back}
            onClick={() => handleGoBack()}
          >
            Voltar para geral
          </Button>
        </div>
      </div>
      <div className={classes.answers_wrapper}>
        {answersByDate?.length > 0 &&
          answersByDate.map(answer => {
            const reportDate = answer[0]
            const reportsValues = answer[1]
            const reportTitle =
              reportsValues?.[0].report_student.report.report_template.title
            const classroom =
              reportsValues?.[0].report_student.report.report_template
                .classroom?.[0].name
            const expanded = expandedRows.find(
              expRow => expRow.date === reportDate
            )

            return (
              <div key={reportDate} className={classes.answer_wrapper}>
                <div className={classes.answer}>
                  <div className={classes.answer_first_column}>
                    <div className={classes.answer_info}>
                      <p className='title'>Título:</p>
                      <p className='value'>{reportTitle}</p>
                    </div>
                  </div>
                  <div className={classes.answer_second_column}>
                    <div className={classes.answer_info}>
                      <p className='title'>Turma:</p>
                      <p className='value'>{classroom}</p>
                    </div>
                    <div className={classes.answer_info}>
                      <p className='title'>Disciplina:</p>
                      <p className='value'>
                        {reportsValues?.[0].subject_id
                          ? reportsValues?.[0]?.subject?.name
                          : '--'}
                      </p>
                    </div>
                    <div className={classes.answer_info}>
                      <p className='title'>Data</p>
                      <div className='date'>{reportDate}</div>
                    </div>
                    <KeyboardArrowDownIcon
                      onClick={event =>
                        handleExpandRow(event.currentTarget, reportDate)
                      }
                      className={`
                      ${classes.expanded_icon} 
                      ${expanded ? classes.rotate_arrow : ''}`}
                    />
                  </div>
                </div>
                <Collapse in={!!expanded} container={expanded?.target}>
                  <div className={classes.answer_collapse_wrapper}>
                    {reportsValues?.length > 0 &&
                      reportsValues.map(answerValue => {
                        const multipleAnswers = answerValue.multiple_answers
                        return (
                          <div
                            key={answerValue.id}
                            className={classes.answer_collapse_root}
                          >
                            <p className='topic'>
                              {answerValue.topic}{' '}
                              {multipleAnswers?.length > 0 ? (
                                <i>{multipleAnswers.join(', ')}</i>
                              ) : (
                                ''
                              )}
                            </p>
                            <div className={classes.answer_collapse}>
                              <p className='answer'>{answerValue.answer}</p>
                              <p className='author'>
                                Respondido por: {answerValue?.user?.name}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </Collapse>
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default SpecificTagView
